import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type FamilyPlan = "free" | "together";

export function useFamilyEntitlement(familyId: string | null) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["family-entitlement", familyId],
    queryFn: async (): Promise<{ plan: FamilyPlan }> => {
      if (!familyId) return { plan: "free" };
      const { data, error } = await supabase
        .from("family_entitlements" as any)
        .select("plan")
        .eq("family_id", familyId)
        .maybeSingle();
      if (error) throw error;
      if (!data) return { plan: "free" };
      return { plan: (data as any).plan as FamilyPlan };
    },
    enabled: !!familyId,
    staleTime: 60_000,
  });

  // Realtime: invalidate family entitlement cache on DB changes
  useEffect(() => {
    if (!familyId) return;
    const channel = supabase
      .channel(`family-entitlement-rt-${familyId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "family_entitlements", filter: `family_id=eq.${familyId}` }, () => {
        queryClient.invalidateQueries({ queryKey: ["family-entitlement", familyId] });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [familyId, queryClient]);

  const familyPlan = data?.plan ?? "free";

  return {
    familyPlan,
    isTogetherFamily: familyPlan === "together",
    isLoading,
  };
}

/** Entitlements per family for many families (e.g. when showing children from multiple families). */
export function useFamilyEntitlementsMap(familyIds: string[]) {
  const queryClient = useQueryClient();
  const deduped = Array.from(new Set(familyIds)).filter(Boolean);
  const key = deduped.slice().sort().join(",");

  const { data, isLoading } = useQuery({
    queryKey: ["family-entitlements-map", key],
    queryFn: async (): Promise<Record<string, FamilyPlan>> => {
      if (deduped.length === 0) return {};
      const { data: rows, error } = await supabase
        .from("family_entitlements" as any)
        .select("family_id, plan")
        .in("family_id", deduped);
      if (error) throw error;
      const map: Record<string, FamilyPlan> = {};
      for (const id of deduped) map[id] = "free";
      for (const r of rows ?? []) map[(r as any).family_id] = (r as any).plan as FamilyPlan;
      return map;
    },
    enabled: deduped.length > 0,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (deduped.length === 0) return;
    const channel = supabase
      .channel(`family-entitlements-map-rt-${key}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "family_entitlements" }, () => {
        queryClient.invalidateQueries({ queryKey: ["family-entitlements-map", key] });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [key, queryClient]);

  return { familyPlanMap: data ?? {}, isLoading };
}
